/** Document texts, accent colour and footer. */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_INVOICE_EMAIL_TEMPLATES,
  DEFAULT_OFFER_EMAIL_TEMPLATES,
  type LocalizedEmailTemplates,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { InputNumber } from "@primereact/ui/inputnumber";
import { Textarea } from "@primereact/ui/textarea";
import { apiGet, apiPatch } from "../../lib/api.js";
import { ErrorPanel, LoadingPanel, MutationError, Section } from "../shared.js";
import { EmailTemplateEditor } from "./email-templates.js";

export type Documents = {
  offerValidityDays: number;
  accentColor: string;
  offerIntroText?: string | null;
  offerTermsText?: string | null;
  invoiceIntroText?: string | null;
  invoiceTermsText?: string | null;
  footerText?: string | null;
  offerEmailTemplates: LocalizedEmailTemplates;
  invoiceEmailTemplates: LocalizedEmailTemplates;
};

export function DocumentsForm() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["settings", "documents"],
    queryFn: () =>
      apiGet<{
        data: Documents;
      }>("/settings/documents").then((response) => response.data),
  });
  const [form, setForm] = useState<Documents | null>(null);
  useEffect(() => {
    if (!query.data) return;
    setForm({
      ...query.data,
      offerEmailTemplates:
        query.data.offerEmailTemplates ?? DEFAULT_OFFER_EMAIL_TEMPLATES,
      invoiceEmailTemplates:
        query.data.invoiceEmailTemplates ?? DEFAULT_INVOICE_EMAIL_TEMPLATES,
    });
  }, [query.data]);
  const save = useMutation({
    mutationFn: () => apiPatch("/settings/documents", form),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["settings", "documents"],
      });
    },
  });
  if (query.isLoading) return <LoadingPanel />;
  if (query.error) return <ErrorPanel error={query.error} />;
  if (!form) return <LoadingPanel />;
  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Section
        title={t("settings.documents", {
          defaultValue: "Documents",
        })}
      >
        <div className="space-y-4">
          <Field
            label={t("settings.validity", {
              defaultValue: "Offer validity days",
            })}
          >
            <InputNumber.Root
              fluid
              className="max-w-xs"
              min={1}
              value={form.offerValidityDays}
              onValueChange={(event: { value: number | null }) =>
                setForm({
                  ...form,
                  offerValidityDays: event.value ?? 1,
                })
              }
            >
              <InputNumber.Input as={Input} />
            </InputNumber.Root>
          </Field>
          <Field
            label={t("settings.accentColor")}
            hint={t("settings.accentColorHint")}
          >
            <input
              type="color"
              value={form.accentColor}
              onChange={(event) =>
                setForm({ ...form, accentColor: event.target.value })
              }
              className="h-10 w-24 cursor-pointer rounded-lg border border-app-border bg-app-surface"
            />
          </Field>
          {(
            [
              ["offerIntroText", 3],
              ["offerTermsText", 4],
              ["invoiceIntroText", 3],
              ["invoiceTermsText", 3],
              ["footerText", 2],
            ] as const
          ).map(([key, rows]) => (
            <Field
              key={key}
              label={t(`settings.documentTexts.${key}`)}
              hint={key === "footerText" ? t("settings.footerHint") : undefined}
            >
              <Textarea
                rows={rows}
                autoResize
                value={form[key] ?? ""}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setForm({ ...form, [key]: event.target.value })
                }
              />
            </Field>
          ))}
        </div>
      </Section>
      <EmailTemplateEditor
        offer={form.offerEmailTemplates}
        invoice={form.invoiceEmailTemplates}
        onOfferChange={(offerEmailTemplates) =>
          setForm({
            ...form,
            offerEmailTemplates,
          })
        }
        onInvoiceChange={(invoiceEmailTemplates) =>
          setForm({
            ...form,
            invoiceEmailTemplates,
          })
        }
      />
      <MutationError error={save.error} />
      <Button type="submit" loading={save.isPending || undefined}>
        {t("common.save")}
      </Button>
    </form>
  );
}
